import type React from 'react';
import { useState } from 'react';

import { Badge, Box, Button, Flex, Icon, Portal, Stack, Text, VStack, useColorModeValue } from '@ecoinvent/ui';
import mixpanel from 'mixpanel-browser';
import { BiChevronDown } from 'react-icons/bi';
import { useNavigate } from 'react-router';

import CopyToClipboardButton from 'components/shared/CopyToClipboardButton';
import { DirectContribution } from 'hooks/data/datasets/directContribution/types';
import useVersionInfo from 'hooks/useVersionInfo';
import { useDatasetSession } from 'pages/Dataset/DatasetSessionContext';
import { MIXPANEL_EVENTS } from 'utilities/constants/mixpanel';
import { formatNumericalValue } from 'utilities/numbers';

const LABEL_COLUMN_WIDTH = '17.5em';

const HorizontalBarChart = ({ exchanges, impactUnit }: { exchanges: DirectContribution[]; impactUnit: string }) => {
  const navigate = useNavigate();
  const { ver, system_model } = useVersionInfo();
  const { increment } = useDatasetSession();
  const sortedData = [...exchanges].sort((a, b) => b.relative_contribution - a.relative_contribution);
  const [displayCount, setDisplayCount] = useState(10);

  const bgColor = useColorModeValue('white', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const bgHoverColor = useColorModeValue('gray.50', 'blackAlpha.200');
  const dividerColor = useColorModeValue('#F1F2F2', '#4e4e4e');
  const cardBgColor = useColorModeValue('white', 'gray.700');
  const cardColor = useColorModeValue('gray.800', 'white');
  const cardBorderColor = useColorModeValue('gray.200', 'gray.600');
  const badgeBgColor = useColorModeValue('gray.100', 'blackAlpha.300');
  const badgeTextColor = useColorModeValue('gray.700', 'gray.200');
  const textBarChartColor = useColorModeValue('green.700', 'green.800');
  const linkColor = useColorModeValue('green.500', 'green.300');

  const [hoveredItem, setHoveredItem] = useState<DirectContribution | null>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [hoveredCopyIndex, setHoveredCopyIndex] = useState<DirectContribution['index'] | null>(null);

  const handleMouseEnter = (item: DirectContribution, event: React.MouseEvent) => {
    setHoveredItem(item);
    setMousePosition({ x: event.clientX, y: event.clientY });
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    setMousePosition({ x: event.clientX, y: event.clientY });
  };

  const handleMouseLeave = () => {
    setHoveredItem(null);
  };

  const handleBarClick = (index: DirectContribution['index']) => {
    mixpanel.track(MIXPANEL_EVENTS.DIRECT_CONTRIBUTION_BAR_NAVIGATION_TRIGGERED);
    increment('impact_assessment_action_count');
    navigate(`/${ver}/${system_model}/dataset/${index}`);
  };

  return (
    <Box bg={bgColor} p={3} width="100%" flex="1" overflow="hidden">
      <VStack spacing={3} align="stretch">
        {sortedData.slice(0, displayCount).map((item) => {
          const percentage = item.relative_contribution * 100;
          const shouldTextBeOutside = percentage < 7;
          const isHovered = hoveredItem?.index === item.index;
          const opacity = hoveredItem && !isHovered ? 0.6 : 1;

          return (
            <Flex
              key={item.index}
              align="center"
              minH="40px"
              my={2}
              onMouseEnter={(e) => handleMouseEnter(item, e)}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
              onClick={() => handleBarClick(item.index)}
              cursor="pointer"
              opacity={opacity}
            >
              {/* Contributing flow text */}
              <Box minW={LABEL_COLUMN_WIDTH} maxW={LABEL_COLUMN_WIDTH} pr={3}>
                <Text
                  fontSize="sm"
                  color={isHovered ? linkColor : textColor}
                  textDecoration={isHovered ? 'underline' : 'none'}
                  noOfLines={2}
                  overflow="hidden"
                  textOverflow="ellipsis"
                  textAlign="right"
                >
                  {formatNumericalValue(item.amount)} {item.unit}{' '}
                  {item.type === 'elementary' ? item.meta.name : item.meta.subcomp}
                </Text>
              </Box>

              {/* Bar container */}
              <Box
                flex="1"
                position="relative"
                mr={4}
                borderTopRightRadius="md"
                borderBottomRightRadius="md"
                _hover={{ bg: bgHoverColor }}
              >
                <Box
                  bg={'green.300'}
                  height="40px"
                  width={`${Math.max(percentage, 2)}%`}
                  borderTopRightRadius="md"
                  borderBottomRightRadius="md"
                  position="relative"
                  display="flex"
                  alignItems="center"
                  px={shouldTextBeOutside ? 0 : 3}
                  _hover={{
                    filter: 'brightness(1.1)',
                  }}
                >
                  {!shouldTextBeOutside && (
                    <Text fontSize="sm" color={textBarChartColor} fontWeight="medium" whiteSpace="nowrap">
                      {percentage.toFixed(item.relative_contribution < 0.01 ? 3 : 1)}%
                    </Text>
                  )}
                </Box>

                {shouldTextBeOutside && (
                  <Text
                    fontSize="sm"
                    color={textColor}
                    fontWeight="medium"
                    position="absolute"
                    left={`${Math.max(percentage, 2) + 1}%`}
                    top="50%"
                    transform="translateY(-50%)"
                    whiteSpace="nowrap"
                  >
                    {percentage.toFixed(item.relative_contribution < 0.01 ? 3 : 1)}%
                  </Text>
                )}
              </Box>

              {/* Impact value */}
              <Box
                display="flex"
                justifyContent="center"
                minW="80px"
                textAlign="right"
                alignItems="center"
                gap={2}
                onMouseEnter={() => setHoveredCopyIndex(item.index)}
                onMouseLeave={() => setHoveredCopyIndex(null)}
              >
                <Text fontSize="sm" color={textColor}>
                  {formatNumericalValue(item.impact)}
                </Text>
                <CopyToClipboardButton
                  value={item.impact}
                  opacity={hoveredCopyIndex === item.index ? 1 : 0}
                  onValueCopied={() => mixpanel.track(MIXPANEL_EVENTS.DIRECT_CONTRIBUTION_IMPACT_VALUE_COPIED)}
                />
              </Box>
            </Flex>
          );
        })}
      </VStack>

      {/* Bottom border above the x-axis labels */}
      <Box borderBottom={`1px solid ${dividerColor}`} mb={2} />

      {/* X-axis labels */}
      <Flex justify="space-between" mt={4} pl={LABEL_COLUMN_WIDTH} mr="84px">
        <Text fontSize="xs" color="gray.500">
          0%
        </Text>
        <Text fontSize="xs" color="gray.500">
          20%
        </Text>
        <Text fontSize="xs" color="gray.500">
          40%
        </Text>
        <Text fontSize="xs" color="gray.500">
          60%
        </Text>
        <Text fontSize="xs" color="gray.500">
          80%
        </Text>
        <Text fontSize="xs" color="gray.500">
          100%
        </Text>
      </Flex>

      {displayCount < exchanges.length && (
        <Stack justifyContent={'center'} pt={6} pb={4}>
          <Button
            variant={'link'}
            rightIcon={<Icon as={BiChevronDown} />}
            size="sm"
            alignSelf={{ base: 'flex-start', lg: 'center' }}
            onClick={() => {
              setDisplayCount((count) => count * 2);
              mixpanel.track(MIXPANEL_EVENTS.DIRECT_CONTRIBUTION_BAR_CHART_SHOW_MORE_CLICKED);
              increment('impact_assessment_action_count');
            }}
          >
            Show {exchanges.length - displayCount} more contributors
          </Button>
        </Stack>
      )}

      {hoveredItem && (
        <Portal>
          <Box
            position="fixed"
            left={
              mousePosition.x + 16 + 400 > window.innerWidth ? `${mousePosition.x - 16 - 400}px` : `${mousePosition.x + 16}px`
            }
            top={`${mousePosition.y + 16}px`}
            bg={cardBgColor}
            border="1px solid"
            borderColor={cardBorderColor}
            borderRadius="md"
            p={6}
            shadow="lg"
            zIndex={1000}
            minW="300px"
            maxW="400px"
            pointerEvents="none"
          >
            {/* Main description */}
            <Text fontSize="md" fontWeight="bold" color={cardColor} mb={4} textDecoration="underline">
              {formatNumericalValue(hoveredItem.amount)} {hoveredItem.unit} {hoveredItem.type} {hoveredItem.meta.name}
            </Text>

            {/* Impact information */}
            <Text fontSize="sm" color="gray.700" mb={2}>
              <Badge mr={1} bg={badgeBgColor} color={badgeTextColor} fontWeight={400} px={2} py={1}>
                +
                <Text as="span" fontWeight="bold">
                  {formatNumericalValue(hoveredItem.impact)}
                </Text>{' '}
                {impactUnit}
              </Badge>
              Impact
            </Text>

            {/* Relative contribution */}
            <Text fontSize="sm" color="gray.700">
              <Badge fontWeight="bold" color={badgeTextColor} mr={1} bg={badgeBgColor} px={2} py={1}>
                {(hoveredItem.relative_contribution * 100).toFixed(1)}%
              </Badge>
              Relative contribution
            </Text>
          </Box>
        </Portal>
      )}
    </Box>
  );
};

export default HorizontalBarChart;
